import { createReducer } from '@reduxjs/toolkit';
import { INeuroCommit, INeuroDocument } from 'neuro-data-structures';
import { evolve, map, mergeRight } from 'ramda';
import { mergeDocument } from 'Utility/documentHandling';
import { reduxActions } from './actions';

const initialState: IDocumentStore = {
  isLoading: undefined,
  isCreating: undefined,
  documents: undefined,
  sortedAndMergedDocuments: undefined,
  removedDocuments: undefined,
  files: undefined,
};

/** Build restore history for a document when it's undeleted */
const makeRestoreHistory = (
  removedDoc: INeuroDocument,
  restoreTS: number,
  uid: string,
  org: string,
): INeuroDocument => {
  const previousHistory = removedDoc.restoreHistory ?? [];
  return {
    ...removedDoc,
    restoreHistory: [
      {
        removeTS: removedDoc.removeTS ?? 0,
        removeDate: removedDoc.removeInfo?.removeDate ?? '',
        remover: removedDoc.removeInfo?.remover ?? '',
        removerOrg: removedDoc.removeInfo?.removerOrg ?? '',
        removeReason: removedDoc.removeInfo?.removeReason,
        restoreTS: restoreTS,
        restorer: uid,
        restorerOrg: org,
      },
      ...previousHistory,
    ],
    removeTS: null,
    removeInfo: null,
  };
};

const reducer = createReducer(initialState, builder => {
  builder
    .addCase(reduxActions.loadAction, state => {
      return { files: [], isLoading: true, documents: [] };
    })
    .addCase(reduxActions.receiveAction, (state, action) => {
      return mergeRight(state, {
        isLoading: false,
        documents: action.payload.documents,
        sortedAndMergedDocuments: action.payload.sortedAndMergedDocuments,
      });
    })
    .addCase(reduxActions.receiveRemovedAction, (state, action) => {
      state.removedDocuments = action.payload.removedDocuments;
    })
    .addCase(reduxActions.clearAction, () => {
      return initialState;
    })
    .addCase(reduxActions.newDocumentAction, (state, action) => {
      const { data } = action.payload;
      const documents = state.documents ? [...state.documents] : [];
      const sortedAndMergedDocuments = state.sortedAndMergedDocuments ? [...state.sortedAndMergedDocuments] : [];

      // TODO: Unsure if data is actually expected to be complete here or not
      // Changing IDocumentStore['documents'] to expect Partial<INeuroDocument>[] instead of INeuroDocument[] breaks stuff elsewhere
      // Either that should be done and resulting errors should be fixed or data should be asserted to be complete here
      documents.unshift(data as INeuroDocument);
      const mergedData = mergeDocument(data);
      mergedData && sortedAndMergedDocuments.unshift(mergedData);
      return mergeRight(state, { documents: documents, sortedAndMergedDocuments: sortedAndMergedDocuments });
    })
    .addCase(reduxActions.deleteDocumentAction, (state, action) => {
      const documents = state.documents ? [...state.documents] : [];
      const sortedAndMergedDocuments = state.sortedAndMergedDocuments ? [...state.sortedAndMergedDocuments] : [];

      // Find the deleted document and if it actually has data
      let validRemovedDoc = documents.find(
        d => d.documentId === action.payload.documentId && d.commits.some(c => c.commitDate !== null),
      );
      validRemovedDoc = validRemovedDoc
        ? { ...validRemovedDoc, removeTS: Date.now(), removeInfo: action.payload.removeInfo }
        : undefined;

      return mergeRight(state, {
        documents: documents.filter(d => d.documentId !== action.payload.documentId),
        sortedAndMergedDocuments: sortedAndMergedDocuments.filter(sd => sd._id !== action.payload.documentId),
        removedDocuments: validRemovedDoc
          ? [...(state.removedDocuments ?? []), validRemovedDoc]
          : state.removedDocuments,
      });
    })
    .addCase(reduxActions.undeleteDocumentAction, (state, action) => {
      const removedDocuments = state.removedDocuments ? [...state.removedDocuments] : [];
      const removedDoc = removedDocuments.find(d => d.documentId === action.payload.documentId);
      if (!removedDoc) return state;

      // Files go to their own arrays in the redux state
      if (removedDoc.documentType === 'filestorefile') {
        const files = state.files ? [...state.files] : [];
        const neuroFiles = state.neuroFiles ? [...state.neuroFiles] : [];
        const commitsMergedRemovedDoc = mergeDocument(removedDoc) as IDocumentWithTypeV2<'filestorefile'>;

        return {
          ...state,
          removedDocuments: removedDocuments.filter(d => d.documentId !== action.payload.documentId),
          neuroFiles: [
            makeRestoreHistory(removedDoc, action.payload.restoreTS, action.payload.userId, action.payload.userOrg),
            ...neuroFiles,
          ],
          files: [commitsMergedRemovedDoc, ...files],
        };
      } else {
        const documents = state.documents ? [...state.documents] : [];
        const sortedAndMergedDocuments = state.sortedAndMergedDocuments ? [...state.sortedAndMergedDocuments] : [];
        const commitsMergedRemovedDoc = mergeDocument(removedDoc) as IDocumentV2;

        return {
          ...state,
          removedDocuments: removedDocuments.filter(d => d.documentId !== action.payload.documentId),
          documents: [
            makeRestoreHistory(removedDoc, action.payload.restoreTS, action.payload.userId, action.payload.userOrg),
            ...documents,
          ],
          sortedAndMergedDocuments: [commitsMergedRemovedDoc, ...sortedAndMergedDocuments],
        };
      }
    })
    .addCase(reduxActions.newCommitAction, (state, action) => {
      const { id, data } = action.payload;
      const documents = state.documents ? [...state.documents] : []; // de-link
      const sortedAndMergedDocuments = state.sortedAndMergedDocuments ? [...state.sortedAndMergedDocuments] : [];

      // Add the new commit to the right document
      documents.forEach(d => d.documentId === id && d.commits.unshift(data));

      const thisMergingDoc = documents.find(d => d.documentId === id);
      const newMergedDocument: IDocumentV2 | undefined | null = thisMergingDoc
        ? mergeDocument(thisMergingDoc)
        : undefined;

      state.documents = documents;
      if (newMergedDocument)
        state.sortedAndMergedDocuments = sortedAndMergedDocuments.map(d => (d._id === id ? newMergedDocument : d));
    })
    .addCase(reduxActions.updateCommitAction, (state, action) => {
      const { id, cid, close, data } = action.payload;
      const documents = state.documents ? [...state.documents] : [];
      const sortedAndMergedDocuments = state.sortedAndMergedDocuments ? [...state.sortedAndMergedDocuments] : [];

      const updatedDocuments = documents.map((d: INeuroDocument) => {
        if (d.documentId === id) {
          const updatedDocument = evolve(
            {
              commits: map((c: INeuroCommit) => {
                return c.commitId === cid
                  ? mergeRight(c, {
                      data,
                      // When closing the commit, set commitDate
                      ...(close ? { commitDate: Date.now() } : {}),
                    })
                  : c;
              }),
            },
            d,
          );

          const updateIndex = sortedAndMergedDocuments.findIndex(d => d._id === id);
          const mergedData = mergeDocument(updatedDocument);
          if (mergedData) {
            sortedAndMergedDocuments[updateIndex] = mergedData;
          }

          return updatedDocument;
        } else return d;
      });

      return mergeRight(state, { documents: updatedDocuments, sortedAndMergedDocuments: sortedAndMergedDocuments });
    })
    .addCase(reduxActions.updateDocumentAction, (state, action) => {
      let documents = state.documents ? [...state.documents] : [];

      // Update old or insert new document
      if (documents.find(d => d.documentId === action.payload.documentId)) {
        documents = (state.documents ?? []).map((d: INeuroDocument) =>
          d.documentId === action.payload.documentId ? action.payload : d,
        );
      } else {
        documents.unshift(action.payload);
      }

      let sortedAndMergedDocuments = state.sortedAndMergedDocuments ? [...state.sortedAndMergedDocuments] : [];
      const newMergedDocument = mergeDocument(action.payload);
      if (!newMergedDocument) return;
      if (sortedAndMergedDocuments.find(d => d._id === action.payload.documentId)) {
        sortedAndMergedDocuments = sortedAndMergedDocuments.map(d =>
          d._id === action.payload.documentId ? newMergedDocument : d,
        );
      } else {
        sortedAndMergedDocuments.unshift(newMergedDocument);
      }

      state.documents = documents;
      state.sortedAndMergedDocuments = sortedAndMergedDocuments;
    })
    .addCase(reduxActions.cancelCommitAction, (state, action) => {
      const { id, cid } = action.payload;
      const documents = state.documents ? [...state.documents] : [];
      const sortedAndMergedDocuments = state.sortedAndMergedDocuments ? [...state.sortedAndMergedDocuments] : [];

      const updatedDocuments = documents.map((d: INeuroDocument) =>
        d.documentId === id ? mergeRight(d, { commits: d.commits.filter(c => c.commitId !== cid) }) : d,
      );

      const updatedDocument = updatedDocuments.find(d => d.documentId === id);
      const newMergedDocument = updatedDocument && mergeDocument(updatedDocument);

      const mergedDocuments = sortedAndMergedDocuments.map(d =>
        d._id === id && newMergedDocument ? newMergedDocument : d,
      );

      return mergeRight(state, {
        documents: updatedDocuments,
        sortedAndMergedDocuments: mergedDocuments,
      });
    })
    .addCase(reduxActions.modifyFieldAction, (state, action) => {
      const { id, commit } = action.payload;
      const documents = state.documents ? [...state.documents] : [];
      const sortedAndMergedDocuments = state.sortedAndMergedDocuments ? [...state.sortedAndMergedDocuments] : [];

      const updatedDocuments = documents.map((d: INeuroDocument) => {
        if (d.documentId === id) {
          const updatedDocument = mergeRight(d, { commits: [...d.commits, commit] });

          const updateIndex = sortedAndMergedDocuments.findIndex(d => d._id === id);
          const mergedData = mergeDocument(updatedDocument);
          if (mergedData) {
            sortedAndMergedDocuments[updateIndex] = mergedData;
          }
          return updatedDocument;
        } else return d;
      });

      return mergeRight(state, { documents: updatedDocuments, sortedAndMergedDocuments: sortedAndMergedDocuments });
    })
    .addCase(reduxActions.modifyLockedFieldAction, (state, action) => {
      const { id, commit } = action.payload;
      const documents = state.documents ? [...state.documents] : [];
      const sortedAndMergedDocuments = state.sortedAndMergedDocuments ? [...state.sortedAndMergedDocuments] : [];

      const updatedDocuments = documents.map((d: INeuroDocument) => {
        if (d.documentId === id) {
          const updatedDocument = mergeRight(d, { commits: [...d.commits, commit] });

          const updateIndex = sortedAndMergedDocuments.findIndex(d => d._id === id);
          const mergedData = mergeDocument(updatedDocument);
          if (mergedData) {
            sortedAndMergedDocuments[updateIndex] = mergedData;
          }

          return updatedDocument;
        } else return d;
      });

      return mergeRight(state, { documents: updatedDocuments, sortedAndMergedDocuments: sortedAndMergedDocuments });
    })
    .addCase(reduxActions.setNewDocAction, (state, action) => {
      const { name, id } = action.payload;
      if (name) state.isCreating = { name, id };
    })
    .addCase(reduxActions.unsetNewDocAction, state => {
      state.isCreating = undefined;
    })

    .addCase(reduxActions.loadFilesMetadataAction, (state, action) => {
      state.files = action.payload.mergedFiles;
      state.neuroFiles = action.payload.files;
    })
    .addCase(reduxActions.newFileMetadataAction, (state, action) => {
      state.files = [...(state.files ?? []), action.payload.mergedFile];
      state.neuroFiles = [...(state.neuroFiles ?? []), action.payload.file];
    })
    .addCase(reduxActions.editFileMetadataAction, (state, action) => {
      const curFiles = [...(state.files ?? [])];
      state.files = curFiles.map(f =>
        f._id === action.payload.documentId ? { ...f, ...action.payload.mergedFile } : f,
      );
      const curNeuroFiles = [...(state.neuroFiles ?? [])];
      state.neuroFiles = curNeuroFiles.map(f =>
        f.documentId === action.payload.documentId ? { ...f, ...action.payload.file } : f,
      );
    })
    .addCase(reduxActions.removeFileMetadataAction, (state, action) => {
      const curFiles = [...(state.files ?? [])];
      const curNeuroFiles = [...(state.neuroFiles ?? [])];
      const removedDoc = curNeuroFiles.find(f => f.documentId === action.payload.id);

      if (removedDoc) {
        state.removedDocuments = [
          ...(state.removedDocuments ?? []),
          {
            ...removedDoc,
            removeTS: action.payload.reason.date,
            removeInfo: {
              removeDate: '',
              remover: action.payload.userId,
              removerOrg: action.payload.orgId,
              removeReason: {
                date: action.payload.reason.reason.date,
                reason: action.payload.reason.reason.reason,
                reasonOther: action.payload.reason.reason.reasonOther,
              },
            },
          },
        ];
        state.files = curFiles.filter(f => f._id !== action.payload.id);
        state.neuroFiles = curNeuroFiles.filter(f => f.documentId !== action.payload.id);
      }
    })

    .addDefaultCase(state => {
      return state;
    });
});

export default reducer;
